import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Icon } from "../ui/icons";
import { StatusChip } from "../ui/StatusChip";
import { LockBadge } from "../ui/LockBadge";
import { Skeleton } from "../ui/Skeleton";
import { supabase } from "../lib/supabase";
import { notifyProjectsChanged } from "../lib/useProjects";
import { assignMeetingToProject, findGlossaryDuplicates, GlossaryDuplicateGroup } from "../lib/api";
import { Toast } from "../components/Toast";
import { promotionToastText } from "../lib/promotionToast";
import { getDefaultWorkspace } from "../lib/workspace";
import { loadActiveProjects, ProjectOption } from "../lib/projects";
import {
  AttendeeFormDialog,
  AttendeeRow,
} from "../components/AttendeeFormDialog";
import {
  GlossaryFormDialog,
  GlossaryRow,
} from "../components/GlossaryFormDialog";
import { logUserEvent } from "../lib/events";
import {
  addIgnore,
  loadBothIgnores,
  removeIgnore,
  IgnoreRow,
} from "../lib/registryIgnores";
import { mergeAttendee, mergeTerm } from "../lib/registryMerge";
import { formatMeetingDate } from "../lib/timestamps";
import { markNewSince, readProjectLastViewed, writeProjectLastViewed } from "../lib/recentlyAdded";

// Project home: header (name + description, inline-edit) + three sections:
// Transcripts (this project's meetings), Attendees, Glossary.
//
// Archived projects render in read-only mode (Restore banner at top;
// Add/Edit/Delete controls hidden).

interface ProjectDetailRow {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  deleted_at: string | null;
}

interface AttendeeFullRow extends AttendeeRow {
  deleted_at: string | null;
  created_at: string;
}

interface GlossaryFullRow extends GlossaryRow {
  deleted_at: string | null;
  created_at: string;
}

interface MeetingMini {
  id: string;
  title: string | null;
  status: string;
  audio_duration_ms: number | null;
  created_at: string;
  recorded_at: string | null;
  locked_at: string | null;
  workspace_id: string;
  project_id: string | null;
  deleted_at: string | null;
}

type AttendeeDialogState =
  | { mode: "create"; defaultProjectId: string | null }
  | { mode: "edit"; row: AttendeeRow }
  | null;

type GlossaryDialogState =
  | { mode: "create"; defaultProjectId: string | null }
  | { mode: "edit"; row: GlossaryRow }
  | null;

function formatDuration(ms: number | null): string {
  if (!ms) return "—";
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatAliases(arr: string[] | null | undefined): string {
  return (arr ?? []).join(", ");
}

export function ProjectDetail() {
  const { id: projectId } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [project, setProject] = useState<ProjectDetailRow | null>(null);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [transcripts, setTranscripts] = useState<MeetingMini[]>([]);
  const [attendees, setAttendees] = useState<AttendeeFullRow[]>([]);
  const [glossary, setGlossary] = useState<GlossaryFullRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Transient post-assign promotion summary (spec §3). Null = no toast.
  const [toast, setToast] = useState<string | null>(null);
  const [attDialog, setAttDialog] = useState<AttendeeDialogState>(null);
  const [gloDialog, setGloDialog] = useState<GlossaryDialogState>(null);

  // Ignored list (registry_ignores scoped to this project).
  const [attendeeIgnores, setAttendeeIgnores] = useState<IgnoreRow[]>([]);
  const [glossaryIgnores, setGlossaryIgnores] = useState<IgnoreRow[]>([]);
  const [ignoredOpen, setIgnoredOpen] = useState(false);

  // Header inline-edit state.
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState("");

  // Merge picker: id of the SOURCE row whose picker is currently open. Null = closed.
  const [attMergingId, setAttMergingId] = useState<string | null>(null);
  const [gloMergingId, setGloMergingId] = useState<string | null>(null);

  // "Find duplicates" scan state for the Glossary section.
  const [dupGroups, setDupGroups] = useState<GlossaryDuplicateGroup[] | null>(null);
  const [dupScanning, setDupScanning] = useState(false);
  const [dupError, setDupError] = useState<string | null>(null);

  // "New since last visit" highlight state.
  const [seenBefore, setSeenBefore] = useState<string | null>(null);
  const [newAttendeeIds, setNewAttendeeIds] = useState<Set<string>>(new Set());
  const [newGlossaryIds, setNewGlossaryIds] = useState<Set<string>>(new Set());
  // Gate the snapshot+write to AT MOST ONCE per projectId. StrictMode's
  // same-instance double-invoke shares this ref, so the second run is skipped
  // (otherwise it would read back the `now` the first run just wrote and
  // collapse seenBefore → no badges in dev). A genuine revisit is a new
  // instance → ref resets → correctly re-snapshots.
  const snapshottedPidRef = useRef<string | null>(null);

  // Inline title rename on transcripts.
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [reassigningId, setReassigningId] = useState<string | null>(null);

  // Section menu — refs for the three section headings + active tracking.
  const transcriptsRef = useRef<HTMLHeadingElement>(null);
  const attendeesRef = useRef<HTMLHeadingElement>(null);
  const glossaryRef = useRef<HTMLHeadingElement>(null);
  type SectionKey = "transcripts" | "attendees" | "glossary";
  const [activeSection, setActiveSection] = useState<SectionKey>("transcripts");

  useEffect(() => {
    const refs = [
      { key: "transcripts" as SectionKey, el: transcriptsRef.current },
      { key: "attendees" as SectionKey, el: attendeesRef.current },
      { key: "glossary" as SectionKey, el: glossaryRef.current },
    ];
    const valid = refs.filter((r) => r.el !== null) as { key: SectionKey; el: Element }[];
    if (valid.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the topmost section heading that is intersecting or has passed the
        // top of the viewport (rootMargin pushes entry boundary down so headings
        // "activate" just as they cross the top chrome).
        const passing = entries.filter((e) => e.isIntersecting);
        if (passing.length > 0) {
          // Pick the one highest in the document (smallest boundingClientRect.top
          // among intersecting entries from this batch is unreliable — use DOM order).
          for (const { key, el } of valid) {
            if (passing.some((e) => e.target === el)) {
              setActiveSection(key);
              break;
            }
          }
        }
      },
      {
        // root: null → viewport; rootMargin: a negative top margin so each
        // heading "enters" only once it's near the top of the scroll area.
        rootMargin: "-10% 0px -80% 0px",
        threshold: 0,
      },
    );

    valid.forEach(({ el }) => observer.observe(el));
    return () => observer.disconnect();
  }, [loading]); // re-run after data loads so refs are attached

  function scrollToSection(ref: React.RefObject<HTMLHeadingElement | null>) {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const readOnly = !!project?.deleted_at;

  const loadAll = useCallback(
    async (ws: string, pid: string) => {
      setLoading(true);
      setError(null);
      const [
        projectRes,
        projectsRes,
        transcriptsRes,
        attRes,
        gloRes,
      ] = await Promise.all([
        supabase
          .from("projects")
          .select("id, workspace_id, name, description, deleted_at")
          .eq("id", pid)
          .maybeSingle(),
        loadActiveProjects(ws),
        supabase
          .from("meetings")
          .select(
            "id, title, status, audio_duration_ms, created_at, recorded_at, locked_at, workspace_id, project_id, deleted_at",
          )
          .eq("workspace_id", ws)
          .eq("project_id", pid)
          .is("deleted_at", null)
          .order("created_at", { ascending: false }),
        supabase
          .from("attendees")
          .select(
            "id, workspace_id, project_id, display_name, role, notes, aliases, deleted_at, created_at",
          )
          .eq("workspace_id", ws)
          .eq("project_id", pid)
          .is("deleted_at", null)
          .order("display_name"),
        supabase
          .from("glossary_terms")
          .select(
            "id, workspace_id, project_id, term, notes, aliases, deleted_at, created_at",
          )
          .eq("workspace_id", ws)
          .eq("project_id", pid)
          .is("deleted_at", null)
          .order("term"),
      ]);

      if (projectRes.error) {
        setError(projectRes.error.message);
        setLoading(false);
        return;
      }
      if (!projectRes.data) {
        setError("Project not found.");
        setLoading(false);
        return;
      }

      setProject(projectRes.data as ProjectDetailRow);
      setProjects(projectsRes);
      setTranscripts((transcriptsRes.data ?? []) as MeetingMini[]);
      setAttendees((attRes.data ?? []) as AttendeeFullRow[]);
      setGlossary((gloRes.data ?? []) as GlossaryFullRow[]);
      setLoading(false);
    },
    [],
  );

  const refreshIgnores = useCallback(async (pid: string) => {
    const { glossary: glo, attendee: att } = await loadBothIgnores(pid);
    setAttendeeIgnores(att);
    setGlossaryIgnores(glo);
  }, []);

  useEffect(() => {
    if (!projectId) return;
    // Clear per-project scan/merge state so navigating to a different project
    // never shows stale "Find duplicates" results or a dangling merge picker
    // from the previous project.
    setDupGroups(null);
    setDupError(null);
    setAttMergingId(null);
    setGloMergingId(null);
    let cancelled = false;
    // Snapshot the stored last-viewed timestamp BEFORE writing now, so the
    // current view compares against the OLD value (sees what's new), while
    // writing now ensures badges clear on the NEXT visit. Gated once per
    // projectId so StrictMode's double-invoke doesn't read back its own write.
    if (snapshottedPidRef.current !== projectId) {
      snapshottedPidRef.current = projectId;
      const stored = readProjectLastViewed(projectId);
      setSeenBefore(stored);
      writeProjectLastViewed(projectId, new Date().toISOString());
    }
    getDefaultWorkspace()
      .then((ws) => {
        if (cancelled) return;
        setWorkspaceId(ws.id);
        return Promise.all([loadAll(ws.id, projectId), refreshIgnores(projectId)]);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, loadAll, refreshIgnores]);

  // Recompute new-since-last-visit sets whenever rows or the snapshot change.
  useEffect(() => {
    setNewAttendeeIds(markNewSince(attendees, seenBefore));
    setNewGlossaryIds(markNewSince(glossary, seenBefore));
  }, [attendees, glossary, seenBefore]);

  // ----- Header edit -----

  function startEditName() {
    if (!project || readOnly) return;
    setNameDraft(project.name);
    setEditingName(true);
  }

  async function commitName() {
    if (!project) return;
    const trimmed = nameDraft.trim();
    setEditingName(false);
    if (!trimmed || trimmed === project.name) return;
    const prev = project;
    setProject({ ...project, name: trimmed });
    const { error: e } = await supabase
      .from("projects")
      .update({ name: trimmed })
      .eq("id", project.id);
    if (e) {
      setProject(prev);
      setError(e.message);
    } else {
      notifyProjectsChanged();
    }
  }

  function startEditDesc() {
    if (!project || readOnly) return;
    setDescDraft(project.description ?? "");
    setEditingDesc(true);
  }

  async function commitDesc() {
    if (!project) return;
    const trimmed = descDraft.trim();
    const newDesc = trimmed || null;
    setEditingDesc(false);
    if (newDesc === (project.description ?? null)) return;
    const prev = project;
    setProject({ ...project, description: newDesc });
    const { error: e } = await supabase
      .from("projects")
      .update({ description: newDesc })
      .eq("id", project.id);
    if (e) {
      setProject(prev);
      setError(e.message);
    }
  }

  // ----- Project soft-delete + undelete -----

  async function softDeleteProject() {
    if (!project) return;
    const ok = window.confirm(
      `Archive project "${project.name}"? It moves out of your active projects ` +
        `but nothing is lost — its attendees, glossary, and transcripts stay, and ` +
        `you can Restore it anytime from the Projects → Archived view.`,
    );
    if (!ok) return;
    const { error: e } = await supabase
      .from("projects")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", project.id);
    if (e) {
      setError(e.message);
      return;
    }
    notifyProjectsChanged();
    nav("/projects");
  }

  async function undeleteProject() {
    if (!project) return;
    const { error: e } = await supabase
      .from("projects")
      .update({ deleted_at: null })
      .eq("id", project.id);
    if (e) {
      setError(e.message);
      return;
    }
    setProject({ ...project, deleted_at: null });
    notifyProjectsChanged();
  }

  // ----- Attendees + Glossary CRUD -----

  async function softDeleteAttendee(row: AttendeeFullRow) {
    const confirmed = window.confirm(
      `Delete attendee "${row.display_name}"? This permanently removes them and won't be re-suggested for this project.`,
    );
    if (!confirmed) return;
    const prev = attendees;
    setAttendees(attendees.filter((r) => r.id !== row.id));
    const { error: e } = await supabase
      .from("attendees")
      .delete()
      .eq("id", row.id);
    if (e) {
      setAttendees(prev);
      setError(e.message);
      return;
    }
    if (workspaceId && project?.id) {
      await addIgnore(workspaceId, project.id, "attendee", row.display_name);
      await refreshIgnores(project.id);
    }
  }

  async function softDeleteGlossary(row: GlossaryFullRow) {
    const confirmed = window.confirm(
      `Delete glossary term "${row.term}"? This permanently removes it and won't be re-suggested for this project.`,
    );
    if (!confirmed) return;
    const prev = glossary;
    setGlossary(glossary.filter((r) => r.id !== row.id));
    const { error: e } = await supabase
      .from("glossary_terms")
      .delete()
      .eq("id", row.id);
    if (e) {
      setGlossary(prev);
      setError(e.message);
      return;
    }
    if (workspaceId && project?.id) {
      await addIgnore(workspaceId, project.id, "glossary", row.term);
      await refreshIgnores(project.id);
    }
  }

  async function mergeAttendeeInto(source: AttendeeFullRow, target: AttendeeFullRow) {
    setAttMergingId(null);
    if (!workspaceId || !project?.id || source.id === target.id) return;
    if (!window.confirm(
      `Fold "${source.display_name}" into "${target.display_name}"? Its name and aliases ` +
      `move onto "${target.display_name}", and the "${source.display_name}" row is removed.`,
    )) return;
    const merged = mergeAttendee(target, source);
    const prev = attendees;
    setAttendees(attendees.filter((r) => r.id !== source.id)
      .map((r) => (r.id === target.id ? { ...r, aliases: merged.aliases } : r)));
    const { error: updateErr } = await supabase.from("attendees").update({ aliases: merged.aliases }).eq("id", target.id);
    if (updateErr) { setAttendees(prev); setError(`Merge failed: ${updateErr.message}`); return; }
    const { error: delErr } = await supabase.from("attendees").delete().eq("id", source.id);
    if (delErr) {
      await supabase.from("attendees").update({ aliases: target.aliases ?? [] }).eq("id", target.id);
      setAttendees(prev); setError(`Merge failed: ${delErr.message}`); return;
    }
    await addIgnore(workspaceId, project.id, "attendee", source.display_name);
    await refreshIgnores(project.id);
  }

  async function mergeGlossaryInto(source: GlossaryFullRow, target: GlossaryFullRow) {
    setGloMergingId(null);
    if (!workspaceId || !project?.id || source.id === target.id) return;
    if (!window.confirm(
      `Fold "${source.term}" into "${target.term}"? Its spelling and aliases move onto ` +
      `"${target.term}", and the "${source.term}" row is removed.`,
    )) return;
    const merged = mergeTerm(target, source);
    const prev = glossary;
    setGlossary(glossary.filter((r) => r.id !== source.id)
      .map((r) => (r.id === target.id ? { ...r, aliases: merged.aliases } : r)));
    const { error: updateErr } = await supabase.from("glossary_terms").update({ aliases: merged.aliases }).eq("id", target.id);
    if (updateErr) { setGlossary(prev); setError(`Merge failed: ${updateErr.message}`); return; }
    const { error: delErr } = await supabase.from("glossary_terms").delete().eq("id", source.id);
    if (delErr) {
      await supabase.from("glossary_terms").update({ aliases: target.aliases ?? [] }).eq("id", target.id);
      setGlossary(prev); setError(`Merge failed: ${delErr.message}`); return;
    }
    await addIgnore(workspaceId, project.id, "glossary", source.term);
    await refreshIgnores(project.id);
  }

  async function unignore(row: IgnoreRow) {
    try {
      await removeIgnore(row.id);
      if (project?.id) await refreshIgnores(project.id);
    } catch (e) {
      setError(`Un-ignore failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  // ----- Transcripts: inline rename + reassign + soft-delete -----

  function startEditTranscriptTitle(t: MeetingMini) {
    setEditingTitleId(t.id);
    setEditingTitle(t.title ?? "");
  }

  async function commitTranscriptTitle(t: MeetingMini) {
    if (editingTitleId !== t.id) return;
    const trimmed = editingTitle.trim();
    const effectiveTitle =
      trimmed ||
      `Untitled transcript ${new Date(t.created_at).toISOString().slice(0, 10)}`;
    setEditingTitleId(null);
    setEditingTitle("");
    if (effectiveTitle === t.title) return;

    const prev = transcripts;
    setTranscripts(
      transcripts.map((r) =>
        r.id === t.id ? { ...r, title: effectiveTitle } : r,
      ),
    );
    const { error: e } = await supabase
      .from("meetings")
      .update({ title: effectiveTitle })
      .eq("id", t.id);
    if (e) {
      setTranscripts(prev);
      setError(e.message);
      return;
    }
    try {
      await logUserEvent({
        meetingId: t.id,
        workspaceId: t.workspace_id,
        eventType: "title_change",
        payload: { old_title: t.title, new_title: effectiveTitle },
      });
    } catch {
      /* best-effort */
    }
  }

  async function reassignTranscript(
    t: MeetingMini,
    newProjectId: string | null,
  ) {
    if (newProjectId === t.project_id) {
      setReassigningId(null);
      return;
    }

    // Confirm before committing the change.
    const currentProjectName = t.project_id
      ? (projects.find((p) => p.id === t.project_id)?.name ??
         (t.project_id === projectId ? project?.name : undefined) ??
         "this project")
      : null;
    const targetName = newProjectId
      ? (projects.find((p) => p.id === newProjectId)?.name ??
         (newProjectId === projectId ? project?.name : undefined) ??
         "the project")
      : null;
    const message = newProjectId
      ? `Move this meeting to "${targetName}"?`
      : `Unassign this meeting from "${currentProjectName}"?`;

    if (!window.confirm(message)) {
      setReassigningId(null);
      return;
    }

    setReassigningId(null);
    const prev = transcripts;
    // Optimistic: row leaves this view if reassigned away.
    if (newProjectId !== projectId) {
      setTranscripts(transcripts.filter((r) => r.id !== t.id));
    }
    try {
      // The endpoint updates project_id, logs project_change server-side,
      // and promotes names/terms into the destination project's registry.
      const res = await assignMeetingToProject(t.id, newProjectId);
      // Quiet confirmation of what the top-up added (spec §3). Only when
      // assigning TO a project (un-assigning to null has no destination).
      if (newProjectId) {
        const projectName =
          (newProjectId === projectId ? project?.name : undefined) ??
          projects.find((p) => p.id === newProjectId)?.name ??
          "the project";
        setToast(
          res.promotion_warning ??
            promotionToastText(res.attendees_promoted, res.terms_promoted, projectName),
        );
      }
    } catch (err) {
      setTranscripts(prev);
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function softDeleteTranscript(t: MeetingMini) {
    const prev = transcripts;
    setTranscripts(transcripts.filter((r) => r.id !== t.id));
    const { error: e } = await supabase
      .from("meetings")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", t.id);
    if (e) {
      setTranscripts(prev);
      setError(e.message);
      return;
    }
    try {
      await logUserEvent({
        meetingId: t.id,
        workspaceId: t.workspace_id,
        eventType: "transcript_deleted",
        payload: {},
      });
    } catch {
      /* best-effort */
    }
  }

  if (loading) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 16 }}>
      <Skeleton height={28} width="40%" />
      <Skeleton height={16} width="60%" />
      <Skeleton height={16} width="50%" />
    </div>
  );
  if (error)
    return (
      <>
        <p className="error">{error}</p>
        <p>
          <Link to="/projects" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Icon name="chevron-left" size={14} aria-hidden />Back to projects
          </Link>
        </p>
      </>
    );
  if (!project || !workspaceId) return null;

  return (
    <>
      <p style={{ fontSize: 13, marginBottom: 8 }}>
        <Link to="/projects" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Icon name="chevron-left" size={13} aria-hidden />Projects
        </Link>
      </p>

      {readOnly && (
        <div
          className="card"
          style={{
            border: "1px solid var(--accent)",
            background: "rgba(255, 200, 50, 0.08)",
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span>
            This project is archived. Its attendees + glossary are preserved
            but don't flow into new meetings. Read only.
          </span>
          <button type="button" onClick={undeleteProject}>
            Restore
          </button>
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        {editingName ? (
          <input
            autoFocus
            type="text"
            dir="auto"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitName();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                setEditingName(false);
              }
            }}
            style={{
              fontSize: 24,
              padding: "4px 8px",
              background: "var(--surface)",
              color: "var(--text)",
              border: "1px solid var(--accent)",
              borderRadius: 4,
              width: "100%",
              maxWidth: 600,
            }}
          />
        ) : (
          <h2
            style={{
              margin: 0,
              cursor: readOnly ? "default" : "pointer",
            }}
            onClick={startEditName}
            title={readOnly ? "" : "Click to rename"}
          >
            {project.name}
          </h2>
        )}

        <div style={{ marginTop: 8 }}>
          {editingDesc ? (
            <textarea
              autoFocus
              dir="auto"
              rows={3}
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              onBlur={commitDesc}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  setEditingDesc(false);
                }
              }}
              style={{
                width: "100%",
                maxWidth: 720,
                padding: "6px 10px",
                background: "var(--surface)",
                color: "var(--text)",
                border: "1px solid var(--accent)",
                borderRadius: 4,
                fontSize: 14,
              }}
            />
          ) : (
            <p
              className="muted"
              style={{
                fontSize: 14,
                margin: 0,
                cursor: readOnly ? "default" : "pointer",
                whiteSpace: "pre-wrap",
              }}
              onClick={startEditDesc}
              title={readOnly ? "" : "Click to edit description"}
            >
              {project.description ||
                (readOnly ? "—" : "Add a description (click to edit)…")}
            </p>
          )}
        </div>

      </div>

      {/* ===== Sticky section menu ===== */}
      <div
        className="tabs-list project-section-menu"
        role="tablist"
        aria-label="Project sections"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "var(--bg)",
          marginBottom: 16,
          marginInline: "calc(-1 * var(--page-pad, 24px))",
          paddingInline: "var(--page-pad, 24px)",
        }}
        data-testid="project-section-menu"
      >
        <button
          type="button"
          role="tab"
          className="tab-btn"
          aria-selected={activeSection === "transcripts"}
          onClick={() => scrollToSection(transcriptsRef)}
          data-testid="section-tab-transcripts"
        >
          Transcripts ({transcripts.length})
        </button>
        <button
          type="button"
          role="tab"
          className="tab-btn"
          aria-selected={activeSection === "attendees"}
          onClick={() => scrollToSection(attendeesRef)}
          data-testid="section-tab-attendees"
        >
          Attendees ({attendees.length})
        </button>
        <button
          type="button"
          role="tab"
          className="tab-btn"
          aria-selected={activeSection === "glossary"}
          onClick={() => scrollToSection(glossaryRef)}
          data-testid="section-tab-glossary"
        >
          Glossary ({glossary.length})
        </button>
      </div>

      {/* ===== Transcripts section ===== */}
      <section style={{ marginBottom: 32 }}>
        <h3
          ref={transcriptsRef}
          style={{ marginBottom: 12, scrollMarginTop: "calc(var(--sticky-header-h, 64px) + 48px)" }}
        >
          Transcripts{" "}
          <span className="muted" style={{ fontSize: 14, fontWeight: 400 }}>
            ({transcripts.length})
          </span>
        </h3>
        {transcripts.length === 0 ? (
          <p className="muted">
            No transcripts in this project yet. Upload one from{" "}
            <Link to="/meetings/new">New transcript</Link> and pick this
            project from the picker, or reassign an existing transcript from
            the <Link to="/">Transcripts list</Link>.
          </p>
        ) : (
          <div className="card">
            <table className="meetings">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Duration</th>
                  <th>Meeting date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {transcripts.map((t) => (
                  <tr key={t.id}>
                    <td>
                      {editingTitleId === t.id ? (
                        <input
                          autoFocus
                          type="text"
                          dir="auto"
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          onBlur={() => commitTranscriptTitle(t)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              commitTranscriptTitle(t);
                            }
                            if (e.key === "Escape") {
                              e.preventDefault();
                              setEditingTitleId(null);
                            }
                          }}
                          style={{
                            width: "100%",
                            minWidth: 160,
                            padding: "2px 6px",
                            background: "var(--surface)",
                            color: "var(--text)",
                            border: "1px solid var(--accent)",
                            borderRadius: 3,
                            fontSize: 14,
                          }}
                        />
                      ) : reassigningId === t.id ? (
                        <select
                          autoFocus
                          value={t.project_id ?? ""}
                          onChange={(e) =>
                            reassignTranscript(
                              t,
                              e.target.value === "" ? null : e.target.value,
                            )
                          }
                          onBlur={() => setReassigningId(null)}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              e.preventDefault();
                              setReassigningId(null);
                            }
                          }}
                          style={{
                            padding: "2px 6px",
                            background: "var(--surface)",
                            color: "var(--text)",
                            border: "1px solid var(--accent)",
                            borderRadius: 3,
                            fontSize: 13,
                          }}
                        >
                          <option value="">Unassigned</option>
                          {projects.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <Link to={`/meetings/${t.id}`}>
                            {t.title ?? "(untitled)"}
                          </Link>
                          {t.locked_at && <LockBadge />}
                          {!readOnly && (
                            <>
                              <button
                                type="button"
                                className="icon-btn-kit"
                                onClick={() => startEditTranscriptTitle(t)}
                                title="Rename"
                                style={{ padding: "3px 5px" }}
                              >
                                <Icon name="edit" size={13} aria-hidden />
                              </button>
                              <button
                                type="button"
                                className="icon-btn-kit"
                                onClick={() => setReassigningId(t.id)}
                                title="Move to a different project"
                                style={{ padding: "3px 5px" }}
                              >
                                <Icon name="folder" size={13} aria-hidden />
                              </button>
                            </>
                          )}
                        </span>
                      )}
                    </td>
                    <td>
                      <StatusChip status={t.status} lockedAt={t.locked_at} />
                    </td>
                    <td>{formatDuration(t.audio_duration_ms)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {t.recorded_at && (
                        <div>{formatMeetingDate(t.recorded_at)}</div>
                      )}
                      <div style={{ fontSize: 11, color: "var(--text-muted, #888)" }}>
                        Added {new Date(t.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {!readOnly && (
                        <button
                          type="button"
                          className="secondary"
                          onClick={() => softDeleteTranscript(t)}
                          style={{
                            fontSize: 12,
                            padding: "2px 8px",
                            color: "var(--error, #e55)",
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ===== Attendees section ===== */}
      <section style={{ marginBottom: 32 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h3
            ref={attendeesRef}
            style={{ margin: 0, scrollMarginTop: "calc(var(--sticky-header-h, 64px) + 48px)" }}
          >
            Attendees{" "}
            <span className="muted" style={{ fontSize: 14, fontWeight: 400 }}>
              ({attendees.length})
            </span>
            {newAttendeeIds.size > 0 && (
              <span className="muted" style={{ fontSize: 12, marginInlineStart: 8 }}>
                · {newAttendeeIds.size} added since your last visit
              </span>
            )}
          </h3>
          {!readOnly && (
            <button
              type="button"
              onClick={() =>
                setAttDialog({ mode: "create", defaultProjectId: projectId! })
              }
            >
              + Add attendee
            </button>
          )}
        </div>
        {attendees.length === 0 ? (
          <p className="muted">
            No attendees scoped to this project yet.{" "}
            {!readOnly &&
              `Click "Add attendee" to add the people who appear in this project's meetings.`}
          </p>
        ) : (
          <div className="card">
            <table className="meetings">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Aliases</th>
                  <th>Notes</th>
                  {!readOnly && <th style={{ width: 220 }} />}
                </tr>
              </thead>
              <tbody>
                {attendees.map((row) => (
                  <tr key={row.id}>
                    <td dir="auto">
                      <strong>{row.display_name}</strong>
                      {newAttendeeIds.has(row.id) && (
                        <span style={{ marginInlineStart: 6, fontSize: 10, padding: "1px 5px", borderRadius: 4,
                          background: "var(--accent)", color: "var(--on-accent)", fontWeight: 600 }}>new</span>
                      )}
                    </td>
                    <td dir="auto">{row.role || ""}</td>
                    <td
                      dir="auto"
                      style={{ fontSize: 13, color: "var(--text-muted)" }}
                    >
                      {formatAliases(row.aliases) || "—"}
                    </td>
                    <td dir="auto" style={{ fontSize: 13 }}>
                      {row.notes || ""}
                    </td>
                    {!readOnly && (
                      <td>
                        {attMergingId === row.id ? (
                          (() => {
                            const targets = attendees.filter((r) => r.id !== row.id);
                            return (
                              <select
                                autoFocus
                                defaultValue=""
                                onChange={(e) => {
                                  const target = targets.find((r) => r.id === e.target.value);
                                  if (target) void mergeAttendeeInto(row, target);
                                  else setAttMergingId(null);
                                }}
                                onBlur={() => setAttMergingId(null)}
                                style={{
                                  padding: "4px 8px",
                                  background: "var(--surface)",
                                  color: "var(--text)",
                                  border: "1px solid var(--accent)",
                                  borderRadius: 4,
                                  fontSize: 12,
                                  maxWidth: 180,
                                }}
                              >
                                <option value="" disabled>
                                  {targets.length
                                    ? "Merge into…"
                                    : "No other attendees in this project"}
                                </option>
                                {targets.map((r) => (
                                  <option key={r.id} value={r.id}>
                                    {r.display_name}
                                  </option>
                                ))}
                              </select>
                            );
                          })()
                        ) : (
                          <>
                            <button
                              type="button"
                              className="secondary"
                              onClick={() => setAttDialog({ mode: "edit", row })}
                              style={{
                                padding: "4px 10px",
                                fontSize: 12,
                                marginRight: 6,
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="secondary"
                              onClick={() => setAttMergingId(row.id)}
                              title="Fold this person into another row (same person, different spelling)"
                              style={{
                                padding: "4px 10px",
                                fontSize: 12,
                                marginRight: 6,
                              }}
                            >
                              Merge into…
                            </button>
                            <button
                              type="button"
                              className="secondary"
                              onClick={() => softDeleteAttendee(row)}
                              style={{ padding: "4px 10px", fontSize: 12 }}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ===== Glossary section ===== */}
      <section style={{ marginBottom: 32 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h3
            ref={glossaryRef}
            style={{ margin: 0, scrollMarginTop: "calc(var(--sticky-header-h, 64px) + 48px)" }}
          >
            Glossary{" "}
            <span className="muted" style={{ fontSize: 14, fontWeight: 400 }}>
              ({glossary.length})
            </span>
            {newGlossaryIds.size > 0 && (
              <span className="muted" style={{ fontSize: 12, marginInlineStart: 8 }}>
                · {newGlossaryIds.size} added since your last visit
              </span>
            )}
          </h3>
          {!readOnly && (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                type="button"
                className="secondary"
                disabled={dupScanning}
                onClick={async () => {
                  if (!projectId) return;
                  setDupScanning(true);
                  setDupError(null);
                  setDupGroups(null);
                  try {
                    setDupGroups(await findGlossaryDuplicates(projectId));
                  } catch {
                    setDupError("Couldn't scan — try again.");
                  } finally {
                    setDupScanning(false);
                  }
                }}
              >
                {dupScanning ? "Scanning…" : "Find duplicates"}
              </button>
              <button
                type="button"
                onClick={() =>
                  setGloDialog({ mode: "create", defaultProjectId: projectId! })
                }
              >
                + Add term
              </button>
            </div>
          )}
        </div>
        {dupError && <p className="error">{dupError}</p>}
        {dupGroups !== null && (
          <div className="card" style={{ marginBottom: 12 }}>
            <h4 style={{ marginTop: 0, fontSize: 13 }}>Possible duplicates</h4>
            {dupGroups.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>No duplicates found.</p>
            ) : (
              dupGroups.map((g, gi) => (
                <div key={gi} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                  <span dir="auto">
                    {g.terms.map((t, i) => (
                      <span key={i}><strong>{t}</strong>{i < g.terms.length - 1 ? " · " : ""}</span>
                    ))}
                  </span>
                  <span className="muted" style={{ fontSize: 12 }}>{g.reason}</span>
                  <button
                    type="button"
                    className="secondary"
                    style={{ padding: "2px 8px", fontSize: 12 }}
                    onClick={() => {
                      const first = glossary.find((r) => r.id === g.member_ids[0]);
                      if (first) setGloMergingId(first.id);
                    }}
                  >
                    Merge…
                  </button>
                </div>
              ))
            )}
          </div>
        )}
        {glossary.length === 0 ? (
          <p className="muted">
            No glossary terms scoped to this project yet.{" "}
            {!readOnly &&
              `Click "Add term" to add proper nouns + industry terms specific to this project.`}
          </p>
        ) : (
          <div className="card">
            <table className="meetings">
              <thead>
                <tr>
                  <th>Term</th>
                  <th>Aliases</th>
                  <th>Notes</th>
                  {!readOnly && <th style={{ width: 220 }} />}
                </tr>
              </thead>
              <tbody>
                {glossary.map((row) => (
                  <tr key={row.id}>
                    <td dir="auto">
                      <strong>{row.term}</strong>
                      {newGlossaryIds.has(row.id) && (
                        <span style={{ marginInlineStart: 6, fontSize: 10, padding: "1px 5px", borderRadius: 4,
                          background: "var(--accent)", color: "var(--on-accent)", fontWeight: 600 }}>new</span>
                      )}
                    </td>
                    <td
                      dir="auto"
                      style={{ fontSize: 13, color: "var(--text-muted)" }}
                    >
                      {formatAliases(row.aliases) || "—"}
                    </td>
                    <td dir="auto" style={{ fontSize: 13 }}>
                      {row.notes || ""}
                    </td>
                    {!readOnly && (
                      <td>
                        {gloMergingId === row.id ? (
                          (() => {
                            const targets = glossary.filter((r) => r.id !== row.id);
                            return (
                              <select
                                autoFocus
                                defaultValue=""
                                onChange={(e) => {
                                  const target = targets.find((r) => r.id === e.target.value);
                                  if (target) void mergeGlossaryInto(row, target);
                                  else setGloMergingId(null);
                                }}
                                onBlur={() => setGloMergingId(null)}
                                style={{
                                  padding: "4px 8px",
                                  background: "var(--surface)",
                                  color: "var(--text)",
                                  border: "1px solid var(--accent)",
                                  borderRadius: 4,
                                  fontSize: 12,
                                  maxWidth: 180,
                                }}
                              >
                                <option value="" disabled>
                                  {targets.length
                                    ? "Merge into…"
                                    : "No other terms in this project"}
                                </option>
                                {targets.map((r) => (
                                  <option key={r.id} value={r.id}>
                                    {r.term}
                                  </option>
                                ))}
                              </select>
                            );
                          })()
                        ) : (
                          <>
                            <button
                              type="button"
                              className="secondary"
                              onClick={() => setGloDialog({ mode: "edit", row })}
                              style={{
                                padding: "4px 10px",
                                fontSize: 12,
                                marginRight: 6,
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="secondary"
                              onClick={() => setGloMergingId(row.id)}
                              title="Fold this term into another row (same term, different spelling)"
                              style={{
                                padding: "4px 10px",
                                fontSize: 12,
                                marginRight: 6,
                              }}
                            >
                              Merge into…
                            </button>
                            <button
                              type="button"
                              className="secondary"
                              onClick={() => softDeleteGlossary(row)}
                              style={{ padding: "4px 10px", fontSize: 12 }}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ===== Ignored list ===== */}
      {(() => {
        const allIgnores = [...attendeeIgnores, ...glossaryIgnores];
        return (
          <section style={{ marginBottom: 32 }}>
            <button
              type="button"
              onClick={() => setIgnoredOpen((o) => !o)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: "var(--text-muted)",
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <span style={{ fontFamily: "monospace" }}>
                {ignoredOpen ? "▾" : "▸"}
              </span>
              Ignored ({allIgnores.length})
            </button>
            {ignoredOpen && allIgnores.length > 0 && (
              <div
                style={{
                  marginTop: 8,
                  padding: "8px 12px",
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: 4,
                  fontSize: 13,
                }}
              >
                {allIgnores.map((row) => (
                  <div
                    key={row.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      paddingBlock: 3,
                    }}
                  >
                    <span style={{ color: "var(--text-muted)", fontSize: 11 }}>
                      {row.kind === "attendee" ? "person" : "term"}
                    </span>
                    <span>{row.value_display}</span>
                    {!readOnly && (
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => unignore(row)}
                        style={{ padding: "2px 8px", fontSize: 11 }}
                      >
                        Un-ignore
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })()}

      {!readOnly && (
        <div
          style={{
            marginTop: 40,
            paddingTop: 16,
            borderTop: "1px solid var(--border, #333)",
          }}
        >
          <button
            type="button"
            className="secondary"
            onClick={softDeleteProject}
            style={{ fontSize: 12, padding: "4px 10px" }}
          >
            Archive project
          </button>
        </div>
      )}

      <AttendeeFormDialog
        state={attDialog}
        workspaceId={workspaceId}
        projects={projects}
        onClose={() => setAttDialog(null)}
        onSaved={() => {
          setAttDialog(null);
          if (projectId) loadAll(workspaceId, projectId);
        }}
      />
      <GlossaryFormDialog
        state={gloDialog}
        workspaceId={workspaceId}
        projects={projects}
        onClose={() => setGloDialog(null)}
        onSaved={() => {
          setGloDialog(null);
          if (projectId) loadAll(workspaceId, projectId);
        }}
      />
      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </>
  );
}
